/**
 * Notification Context
 * Manages global notification state and integrates with WebSocket events
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { realtimeClient, EVENTS } from '../services/realtimeClient';
import Logger from '../utils/logger';
import { showDesktopNotification } from '../utils/desktopNotification';
import { playNotificationSound } from '../utils/notificationSound';
import {
  getNotificationHistory,
  markNotificationAsRead as apiMarkAsRead,
  markAllNotificationsAsRead as apiMarkAllAsRead
} from '../services/notificationApi';

const NotificationContext = createContext(null);

// Default number of notifications to fetch per page
const PAGE_SIZE = 20;

// Maximum number of notifications to keep in memory
const MAX_NOTIFICATIONS = 200;

// Notification type mappings from WebSocket events
const _EVENT_TYPE_MAP = {
  [EVENTS.BUS_STATUS_UPDATE]: 'bus-maintenance',
  [EVENTS.SCHEDULE_CREATED]: 'schedule-change',
  [EVENTS.SCHEDULE_UPDATED]: 'schedule-change',
  [EVENTS.SCHEDULE_DELETED]: 'schedule-change',
  [EVENTS.MAINTENANCE_CREATED]: 'maintenance-alert',
  [EVENTS.MAINTENANCE_UPDATED]: 'maintenance-alert',
  [EVENTS.MAINTENANCE_ALERT]: 'maintenance-alert',
  [EVENTS.DASHBOARD_REFRESH]: 'system-alert'
};

// Default notification preferences
const DEFAULT_PREFERENCES = {
  enableDesktopNotifications: true,
  enableSoundAlerts: false,
  soundVolume: 0.6, // medium
  enabledEventTypes: {
    'schedule-conflict': true,
    'bus-maintenance': true,
    'driver-status': true,
    'schedule-change': true,
    'system-alert': true
  }
};

/**
 * Get notification preferences from localStorage
 */
function getPreferences() {
  try {
    const stored = localStorage.getItem('notification-preferences');
    if (stored) {
      return { ...DEFAULT_PREFERENCES, ...JSON.parse(stored) };
    }
  } catch (error) {
    Logger.error('Failed to load notification preferences', error);
  }
  return DEFAULT_PREFERENCES;
}

/**
 * Save notification preferences to localStorage
 */
function savePreferences(preferences) {
  try {
    localStorage.setItem('notification-preferences', JSON.stringify(preferences));
  } catch (error) {
    Logger.error('Failed to save notification preferences', error);
  }
}

/**
 * Get unread count from localStorage
 */
function getUnreadCount() {
  try {
    const count = localStorage.getItem('notification-unread-count');
    return count ? parseInt(count, 10) : 0;
  } catch (_error) {
    return 0;
  }
}

/**
 * Save unread count to localStorage
 */
function saveUnreadCount(count) {
  try {
    localStorage.setItem('notification-unread-count', count.toString());
  } catch (error) {
    Logger.error('Failed to save unread count', error);
  }
}

export function NotificationProvider({ children }) {
  const { instance } = useMsal();
  const [notifications, setNotifications] = useState([]);
  const [preferences, setPreferences] = useState(getPreferences);
  const [unreadCount, setUnreadCount] = useState(getUnreadCount);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [continuationToken, setContinuationToken] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  // Check if user is authenticated
  const isAuthenticated = instance.getAllAccounts().length > 0;

  /**
   * Add a new notification
   */
  const addNotification = useCallback(
    notification => {
      const notificationType = notification.type || 'system-alert';

      // Check if this notification type is enabled
      if (!preferences.enabledEventTypes[notificationType]) {
        Logger.debug('Notification type disabled, skipping', { type: notificationType });
        return;
      }

      const newNotification = {
        id: notification.id || `notif-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
        type: notificationType,
        title: notification.title || 'Notification',
        message: notification.message || '',
        severity: notification.severity || 'info',
        timestamp: notification.timestamp || new Date().toISOString(),
        read: false,
        data: notification.data || {}
      };

      setNotifications(prev => {
        // Add to beginning of array (newest first)
        const updated = [newNotification, ...prev];

        // Prune to max notifications
        if (updated.length > MAX_NOTIFICATIONS) {
          return updated.slice(0, MAX_NOTIFICATIONS);
        }

        return updated;
      });

      // Increment unread count
      setUnreadCount(prev => {
        const newCount = prev + 1;
        saveUnreadCount(newCount);
        return newCount;
      });

      // Show desktop notification if enabled and appropriate severity
      if (
        preferences.enableDesktopNotifications &&
        (newNotification.severity === 'error' || newNotification.severity === 'critical')
      ) {
        showDesktopNotification(newNotification.title, newNotification.message, newNotification.severity);
      }

      // Play sound if enabled and appropriate severity
      if (
        preferences.enableSoundAlerts &&
        (newNotification.severity === 'error' || newNotification.severity === 'critical')
      ) {
        playNotificationSound(newNotification.severity, preferences.soundVolume);
      }

      Logger.debug('Notification added', newNotification);
    },
    [preferences]
  );

  /**
   * Mark a notification as read
   */
  const markAsRead = useCallback(async notificationId => {
    // Optimistically update UI
    setNotifications(prev =>
      prev.map(notif => {
        if (notif.id === notificationId && !notif.read) {
          // Decrement unread count
          setUnreadCount(prevCount => {
            const newCount = Math.max(0, prevCount - 1);
            saveUnreadCount(newCount);
            return newCount;
          });
          return { ...notif, read: true };
        }
        return notif;
      })
    );

    // Persist to backend
    try {
      await apiMarkAsRead(notificationId);
      Logger.debug('Notification marked as read in backend', { notificationId });
    } catch (error) {
      Logger.error('Failed to mark notification as read in backend', error);
      // Don't revert UI state - user experience is more important
    }
  }, []);

  /**
   * Re-fetch the first page of notifications and reconcile state.
   * Used after a partial-failure response to ensure UI reflects actual read state.
   */
  const refreshNotifications = useCallback(async () => {
    try {
      const response = await getNotificationHistory({ limit: PAGE_SIZE });
      if (response.notifications) {
        setNotifications(response.notifications);
        const unread = response.notifications.filter(n => !n.read).length;
        setUnreadCount(unread);
        saveUnreadCount(unread);
        setContinuationToken(response.continuationToken);
        setHasMore(response.hasMore);
      }
    } catch (err) {
      Logger.error('Failed to refresh notifications', err);
    }
  }, []);

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = useCallback(async () => {
    // Optimistically update UI
    setNotifications(prev => prev.map(notif => ({ ...notif, read: true })));
    setUnreadCount(0);
    saveUnreadCount(0);

    // Persist to backend
    try {
      const result = await apiMarkAllAsRead();
      if (result.partial) {
        // Some batches failed after retries; re-fetch to show accurate read state
        Logger.warn('Partial failure marking all notifications as read, re-fetching');
        await refreshNotifications();
      } else {
        Logger.debug('All notifications marked as read in backend');
      }
    } catch (error) {
      Logger.error('Failed to mark all notifications as read in backend', error);
      // Don't revert UI state
    }
  }, [refreshNotifications]);

  /**
   * Clear all notifications
   */
  const clearAll = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
    saveUnreadCount(0);
  }, []);

  /**
   * Update preferences
   */
  const updatePreferences = useCallback(newPreferences => {
    setPreferences(prev => {
      const updated = { ...prev, ...newPreferences };
      savePreferences(updated);
      return updated;
    });
  }, []);

  /**
   * Load more notifications (pagination)
   */
  const loadMore = useCallback(async () => {
    if (!hasMore || isLoadingMore || !continuationToken) {
      return;
    }

    try {
      setIsLoadingMore(true);

      Logger.info('Loading more notifications', { continuationToken });

      const response = await getNotificationHistory({
        limit: PAGE_SIZE,
        continuationToken
      });

      if (response.notifications && response.notifications.length > 0) {
        setNotifications(prev => {
          // Append new notifications, avoiding duplicates
          const existingIds = new Set(prev.map(n => n.id));
          const newNotifications = response.notifications.filter(n => !existingIds.has(n.id));
          const updated = [...prev, ...newNotifications];

          // Prune to max notifications if needed
          if (updated.length > MAX_NOTIFICATIONS) {
            return updated.slice(0, MAX_NOTIFICATIONS);
          }

          return updated;
        });

        // Update unread count with new notifications
        const newUnread = response.notifications.filter(n => !n.read).length;
        if (newUnread > 0) {
          setUnreadCount(prev => {
            const newCount = prev + newUnread;
            saveUnreadCount(newCount);
            return newCount;
          });
        }

        Logger.info('More notifications loaded', {
          loaded: response.notifications.length,
          hasMore: response.hasMore
        });
      }

      setContinuationToken(response.continuationToken);
      setHasMore(response.hasMore);
    } catch (error) {
      Logger.error('Failed to load more notifications', error);
      // Don't set error state to avoid disrupting the current view
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasMore, isLoadingMore, continuationToken]);

  /**
   * Load notification history from backend on mount (only if authenticated)
   */
  useEffect(() => {
    // Skip loading notification history if user is not authenticated
    if (!isAuthenticated) {
      Logger.debug('[NotificationContext] Skipping notification history load - user not authenticated');
      setIsLoadingHistory(false);
      return;
    }

    const loadHistory = async () => {
      try {
        setIsLoadingHistory(true);
        setHistoryError(null);

        Logger.info('Loading notification history from backend');

        const response = await getNotificationHistory({
          limit: PAGE_SIZE
        });

        if (response.notifications && response.notifications.length > 0) {
          setNotifications(response.notifications);

          // Calculate unread count from loaded notifications
          const unread = response.notifications.filter(n => !n.read).length;
          setUnreadCount(unread);
          saveUnreadCount(unread);

          // Store pagination state
          setContinuationToken(response.continuationToken);
          setHasMore(response.hasMore);

          Logger.info('Notification history loaded', {
            total: response.notifications.length,
            unread,
            hasMore: response.hasMore
          });
        } else {
          Logger.info('No notification history found');
          setHasMore(false);
          setContinuationToken(null);
        }
      } catch (error) {
        Logger.error('Failed to load notification history', error);
        setHistoryError(error.message);
        // Continue without history - real-time notifications will still work
      } finally {
        setIsLoadingHistory(false);
      }
    };

    loadHistory();
  }, [isAuthenticated]);

  /**
   * Subscribe to WebSocket events
   */
  useEffect(() => {
    if (!realtimeClient.isInitialized) {
      Logger.debug('RealtimeClient not initialized, skipping WebSocket subscription');
      return;
    }

    const unsubscribeFns = [];

    // Subscribe to bus status updates
    unsubscribeFns.push(
      realtimeClient.on(EVENTS.BUS_STATUS_UPDATE, data => {
        if (data?.status === 'Maintenance' || data?.status === 'Out of Service') {
          addNotification({
            type: 'bus-maintenance',
            title: 'Bus Maintenance Alert',
            message: `Bus ${data.busNumber || data.id} is now ${data.status}`,
            severity: 'warning',
            data
          });
        }
      })
    );

    // Subscribe to schedule events
    [EVENTS.SCHEDULE_CREATED, EVENTS.SCHEDULE_UPDATED, EVENTS.SCHEDULE_DELETED].forEach(event => {
      unsubscribeFns.push(
        realtimeClient.on(event, data => {
          const action = event.split(':')[1]; // created, updated, deleted
          addNotification({
            type: 'schedule-change',
            title: `Schedule ${action.charAt(0).toUpperCase() + action.slice(1)}`,
            message: `A schedule has been ${action}`,
            severity: 'info',
            data
          });
        })
      );
    });

    // Subscribe to maintenance alerts
    [EVENTS.MAINTENANCE_CREATED, EVENTS.MAINTENANCE_UPDATED, EVENTS.MAINTENANCE_ALERT].forEach(event => {
      unsubscribeFns.push(
        realtimeClient.on(event, data => {
          addNotification({
            type: 'maintenance-alert',
            title: 'Maintenance Alert',
            message: data.message || 'Maintenance event occurred',
            severity: event === EVENTS.MAINTENANCE_ALERT ? 'warning' : 'info',
            data
          });
        })
      );
    });

    // Subscribe to system alerts
    unsubscribeFns.push(
      realtimeClient.on(EVENTS.DASHBOARD_REFRESH, data => {
        addNotification({
          type: 'system-alert',
          title: 'System Update',
          message: data.message || 'Dashboard data refreshed',
          severity: 'info',
          data
        });
      })
    );

    Logger.info('Subscribed to WebSocket notification events');

    // Cleanup on unmount
    return () => {
      unsubscribeFns.forEach(unsubscribe => unsubscribe());
      Logger.info('Unsubscribed from WebSocket notification events');
    };
  }, [addNotification]);

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      preferences,
      isLoadingHistory,
      isLoadingMore,
      hasMore,
      historyError,
      addNotification,
      markAsRead,
      markAllAsRead,
      clearAll,
      loadMore,
      updatePreferences
    }),
    [
      notifications,
      unreadCount,
      preferences,
      isLoadingHistory,
      isLoadingMore,
      hasMore,
      historyError,
      addNotification,
      markAsRead,
      markAllAsRead,
      clearAll,
      loadMore,
      updatePreferences
    ]
  );

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}

/**
 * Hook to access notification context
 */
export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}

export default NotificationContext;
